import { useState, useCallback, useEffect } from 'react';
import { supabase } from './useSupabase';
import { defaultTemplateConfig, type TemplateConfig } from '@/mocks/template-config';
import { showToast } from '@/components/Toast';

const STORAGE_KEY = 'template-config';

function loadLocalConfig(): TemplateConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as TemplateConfig;
    }
  } catch {
    // ignore
  }
  return defaultTemplateConfig;
}

function saveLocalConfig(config: TemplateConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function useTemplateConfig() {
  const [config, setConfig] = useState<TemplateConfig>(loadLocalConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (data?.template_config) {
        const merged = { ...defaultTemplateConfig, ...(data.template_config as TemplateConfig) };
        setConfig(merged);
        saveLocalConfig(merged);
        setCompanyId(data.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
      showToast('Erro ao carregar configurações do template. Usando padrão local.', 'warning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = useCallback(async (newConfig: TemplateConfig) => {
    setConfig(newConfig);
    saveLocalConfig(newConfig);
    try {
      if (companyId) {
        await supabase
          .from('companies')
          .update({ template_config: newConfig as unknown as Record<string, unknown> })
          .eq('id', companyId);
        showToast('Configurações do template salvas!', 'success');
      } else {
        const { data } = await supabase
          .from('companies')
          .select('id')
          .limit(1)
          .maybeSingle();
        if (data?.id) {
          await supabase
            .from('companies')
            .update({ template_config: newConfig as unknown as Record<string, unknown> })
            .eq('id', data.id);
          setCompanyId(data.id);
          showToast('Configurações do template salvas!', 'success');
        }
      }
    } catch (err) {
      showToast('Erro ao salvar configurações no servidor. Salvas localmente.', 'warning');
    }
  }, [companyId]);

  const updateSection = useCallback(<K extends keyof TemplateConfig>(
    key: K,
    section: TemplateConfig[K]
  ) => {
    const newConfig = { ...config, [key]: section };
    updateConfig(newConfig);
  }, [config, updateConfig]);

  const resetToDefault = useCallback(async () => {
    setConfig(defaultTemplateConfig);
    saveLocalConfig(defaultTemplateConfig);
    try {
      if (companyId) {
        await supabase
          .from('companies')
          .update({ template_config: defaultTemplateConfig as unknown as Record<string, unknown> })
          .eq('id', companyId);
        showToast('Configurações restauradas para o padrão!', 'success');
      }
    } catch (err) {
      showToast('Erro ao restaurar padrão no servidor. Restaurado localmente.', 'warning');
    }
  }, [companyId]);

  return { config, loading, error, updateConfig, updateSection, resetToDefault };
}